import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { FileText } from 'lucide-react';

export function TermsPage() {
  return (
    <>
      <Helmet>
        <title>Terms of Service - Usage Guidelines | Promptliteracy.ai</title>
        <meta name="description" content="Read Promptliteracy.ai's terms of service and usage guidelines for our AI literacy education platform. Learn about educational use, content attribution, and responsible AI practices." />
        
        {/* Open Graph */}
        <meta property="og:title" content="Terms of Service - Usage Guidelines | Promptliteracy.ai" />
        <meta property="og:description" content="Terms of service and usage guidelines for Promptliteracy.ai's AI literacy education platform. Educational use and responsible AI practices." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://promptliteracy.ai/terms" />
        <meta property="og:site_name" content="Promptliteracy.ai" />
        
        {/* Additional SEO */}
        <meta name="keywords" content="terms of service, usage guidelines, educational use policy, content attribution, responsible AI use, AI education terms" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://promptliteracy.ai/terms" />
      </Helmet>

      {/* Header Section */}
      <section className="relative py-20 bg-primary text-white overflow-hidden">
        {/* Background decorations */}
        <div className="absolute top-20 right-20 w-32 h-32 bg-secondary/30 rounded-full blur-3xl animate-pulse-tech" />
        <div className="absolute bottom-20 left-20 w-40 h-40 bg-white/10 rounded-full blur-3xl animate-float" />
        
        <div className="container relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <FileText className="h-8 w-8" />
              <h1 className="text-4xl md:text-5xl font-bold">Terms of Service</h1>
            </div>
            <p className="text-xl opacity-90 leading-relaxed">
              Guidelines for using our educational platform
            </p>
          </div>
        </div>
      </section>
      
      <section className="py-12 bg-primary/3 relative overflow-hidden">
        <div className="absolute inset-0 circuit-pattern opacity-10" />
        <div className="container relative z-10">
        <div className="max-w-4xl mx-auto">
          <Card className="card-slate">
            <CardHeader>
              <CardTitle className="text-2xl text-primary">Usage Guidelines</CardTitle>
            </CardHeader>
            <CardContent className="prose max-w-none">
              <p className="text-muted-foreground mb-4">
                Complete terms of service will be implemented in Phase 3. 
                Currently, our platform is designed for educational use with these guidelines:
              </p>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold mb-2">Educational Use</h3>
                  <p className="text-muted-foreground">
                    Our content is designed for educational purposes and may be used freely in 
                    classrooms, homeschool settings, and self-directed learning environments.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold mb-2">Content Attribution</h3>
                  <p className="text-muted-foreground">
                    When using our materials, please provide appropriate attribution to 
                    PromptLiteracy.ai and respect the original sources we cite.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold mb-2">Responsible AI Use</h3>
                  <p className="text-muted-foreground">
                    We encourage the responsible and ethical use of AI technologies as outlined 
                    in our educational materials and examples.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold mb-2">Questions</h3>
                  <p className="text-muted-foreground">
                    For questions about these terms, please contact us at hello@promptliteracy.ai
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        </div>
      </section>
    </>
  );
}